import type { ReactNode } from "react";

// Shared form building blocks for the marketplace post forms.

export const inputClass = "rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-ink";
export const labelClass = "grid gap-1 text-xs font-bold text-muted";

/** A label-left form row: uppercase label + hint on the left, field(s) on the
 *  right. Rows sit inside a single bordered card (see PostJob/PostLoad). */
export function FormRow({
  label, hint, required, children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="grid gap-2 border-t border-line py-5 first:border-t-0 first:pt-0 sm:grid-cols-[210px_minmax(0,1fr)] sm:gap-6">
      <div className="grid h-max gap-0.5">
        <span className="text-[11px] font-bold uppercase tracking-widest text-muted">
          {label}
          {required && <span className="text-accent-strong"> *</span>}
        </span>
        {hint && <span className="text-xs text-muted">{hint}</span>}
      </div>
      <div className="grid gap-2">{children}</div>
    </div>
  );
}

/** Titled group of related fields. */
export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <fieldset className="grid gap-3 rounded-card border border-line bg-surface p-4">
      <legend className="px-1 text-[11px] font-bold uppercase tracking-widest text-muted">{title}</legend>
      {children}
    </fieldset>
  );
}

/** Labeled <select> from [value, label] option pairs; includes a blank option.
 *  Pass `ariaLabel` when the visible label lives outside (e.g. in a FormRow). */
export function Select({
  label, value, onChange, options, placeholder = "—", ariaLabel,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: [string, string][];
  placeholder?: string;
  ariaLabel?: string;
}) {
  return (
    <label className={labelClass}>
      {label}
      <select aria-label={ariaLabel} className={inputClass} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{placeholder}</option>
        {options.map(([v, l]) => (
          <option key={v} value={v}>{l}</option>
        ))}
      </select>
    </label>
  );
}

/** Single-select chip group for filters (click active chip again to clear). */
export function ChipSelect({
  label, options, value, onChange,
}: {
  label: string;
  options: [string, string][];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="grid gap-1.5 text-xs font-bold text-muted">
      {label}
      <div className="flex flex-wrap gap-1.5">
        {options.map(([v, l]) => {
          const on = value === v;
          return (
            <button
              key={v}
              type="button"
              onClick={() => onChange(on ? "" : v)}
              aria-pressed={on}
              className={`rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors ${
                on ? "border-accent bg-accent text-accent-ink" : "border-line text-muted hover:border-accent"
              }`}
            >
              {l}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Multi-select chips for array fields (endorsements, benefits, requirements…). */
export function CheckboxGroup({
  label, options, selected, onToggle,
}: {
  label: string;
  options: string[];
  selected: string[];
  onToggle: (v: string) => void;
}) {
  return (
    <div className="grid gap-1.5 text-xs font-bold text-muted">
      {label}
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => {
          const on = selected.includes(o);
          return (
            <button
              key={o}
              type="button"
              onClick={() => onToggle(o)}
              aria-pressed={on}
              className={`rounded-full border px-2.5 py-1 text-xs font-semibold capitalize transition-colors ${
                on ? "border-accent bg-accent text-accent-ink" : "border-line text-muted hover:border-accent"
              }`}
            >
              {o.replace(/_/g, " ")}
            </button>
          );
        })}
      </div>
    </div>
  );
}
